"use client";

import Link from "@/app/components/LocalizedLink";

// Component to render text with **bold** markdown syntax
function BoldText({ text }: { text: string }) {
  if (!text) return null;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = /\*\*(.*?)\*\*/g;
  let match;
  let key = 0;
  let hasMatches = false;

  while ((match = regex.exec(text)) !== null) {
    hasMatches = true;
    // Add text before the match
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      if (beforeText) {
        parts.push(beforeText);
      }
    }
    // Add the bold text
    parts.push(
      <strong key={key++} className="font-semibold">
        {match[1]}
      </strong>
    );
    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText) {
      parts.push(remainingText);
    }
  }

  // If no matches found, return original text
  if (!hasMatches) {
    return <>{text}</>;
  }

  return <>{parts}</>;
}

// Component to render rich text with internal/external links and **bold** support.
// Supports:
// - Delimiters: <$$/path-or-url|Label$$> or <$$https://example.com|Label$$>
// - Legacy anchors: <a href="/path-or-url">Label</a>
// External links (http:// or https://) open in new tab with security attributes
export default function RichText({
  text,
  stripLinks = false,
}: {
  text: string;
  stripLinks?: boolean;
}) {
  if (!text) return null;

  type Token =
    | { type: "text"; value: string }
    | { type: "link"; href: string; label: string };

  const tokens: Token[] = [];
  let cursor = 0;

  const delimiterRegex = /<\$\$([^|>]+?)\|(.*?)\$\$>/g; // href | label
  const anchorRegex = /<a\s+[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi; // href, label

  // Helper to find the next earliest match among both patterns from current cursor
  function findNextMatch(fromIndex: number) {
    delimiterRegex.lastIndex = fromIndex;
    anchorRegex.lastIndex = fromIndex;
    const d = delimiterRegex.exec(text);
    the_anchor: {
      // block label for clarity with ts
    }
    const a = anchorRegex.exec(text);
    if (!d && !a) return null;
    if (d && a) {
      return d.index <= a.index
        ? { kind: "delim" as const, match: d }
        : { kind: "anchor" as const, match: a };
    }
    if (d) return { kind: "delim" as const, match: d };
    return { kind: "anchor" as const, match: a as RegExpExecArray };
  }

  while (cursor < text.length) {
    const next = findNextMatch(cursor);
    if (!next) {
      const remaining = text.slice(cursor);
      if (remaining) tokens.push({ type: "text", value: remaining });
      break;
    }
    const match = next.match;
    const start = match.index;
    if (start > cursor) {
      const plain = text.slice(cursor, start);
      if (plain) tokens.push({ type: "text", value: plain });
    }
    if (next.kind === "delim") {
      const href = (match[1] || "").trim();
      const label = match[2] || "";
      tokens.push({ type: "link", href, label });
    } else {
      const href = (match[1] || "").trim();
      const label = match[2] || "";
      tokens.push({ type: "link", href, label });
    }
    cursor = (match.index ?? 0) + match[0].length;
  }

  let key = 0;
  return (
    <>
      {tokens.map(tok => {
        if (tok.type === "text") {
          return <BoldText key={key++} text={tok.value} />;
        }
        if (stripLinks) {
          // Only render the label, stripping link wrappers/delimiters
          return <BoldText key={key++} text={tok.label} />;
        }
        // Check if link is external (starts with http:// or https://)
        const href = tok.href;
        const isExternal =
          href.startsWith("http://") || href.startsWith("https://");

        if (isExternal) {
          // External links use regular <a> tag with target="_blank"
          return (
            <a
              key={key++}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-signal-deep hover:text-signal-ink underline underline-offset-2"
            >
              {/* Allow bold syntax within link labels as well */}
              <BoldText text={tok.label} />
            </a>
          );
        }

        // Internal links use Next.js <Link>
        return (
          <Link
            key={key++}
            href={href}
            className="text-signal-deep hover:text-signal-ink underline underline-offset-2"
          >
            {/* Allow bold syntax within link labels as well */}
            <BoldText text={tok.label} />
          </Link>
        );
      })}
    </>
  );
}
